'use client';

/**
 * Chat message list component
 * Renders messages using Virtuoso for efficient scrolling.
 *
 * Scroll contract:
 * - `initialTopMostItemIndex` anchors a freshly-opened chat at the LATEST
 *   message (not the oldest — the default).
 * - `followOutput` keeps the bottom pinned during streaming ONLY when the user
 *   is already at the bottom; scrolled-up readers are not yanked down.
 * - `startReached` + `firstItemIndex` implement reverse pagination: scrolling to
 *   the top loads older history and holds the viewport anchor while it prepends.
 */

import React, { memo } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import type { UIMessage } from 'ai';
import { ChatMessageItem } from './ChatMessageItem';

interface ChatMessageListProps {
  messages: UIMessage[];
  scrollContainerRef: React.RefObject<VirtuosoHandle | null>;
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  previewData: Record<string, any>;
  previewLoading: Record<string, boolean>;
  previewError: Record<string, boolean>;
  onFetchPreview: (
    toolName: string,
    toolInput: any,
    approvalId: string,
    apiBaseUrl: string
  ) => Promise<void>;
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
  isBusy: boolean;
  apiBaseUrl: string;
  /** Virtuoso reverse-scroll anchor; decremented by the parent as older pages prepend. */
  firstItemIndex: number;
  hasMoreMessages?: boolean;
  isLoadingOlderMessages?: boolean;
  onLoadOlderMessages?: () => Promise<void> | void;
}

function ChatMessageListComponent({
  messages,
  scrollContainerRef,
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  previewData,
  previewLoading,
  previewError,
  onFetchPreview,
  onApprove,
  onDeny,
  isBusy,
  apiBaseUrl,
  firstItemIndex,
  hasMoreMessages = false,
  isLoadingOlderMessages = false,
  onLoadOlderMessages,
}: ChatMessageListProps) {
  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Start a conversation</p>
      </div>
    );
  }

  return (
    <Virtuoso
      ref={scrollContainerRef}
      data={messages}
      className="h-full"
      firstItemIndex={firstItemIndex}
      // Open a chat anchored at the latest message, aligned to the bottom.
      initialTopMostItemIndex={{ index: messages.length - 1, align: 'end' }}
      // Scrolling to the top loads older history (reverse pagination).
      startReached={() => {
        if (hasMoreMessages && !isLoadingOlderMessages) {
          void onLoadOlderMessages?.();
        }
      }}
      itemContent={(_index: number, message: UIMessage) => {
        const isLastMessage = message.id === messages[messages.length - 1]?.id;
        return (
          <ChatMessageItem
            message={message}
            messageIndex={_index}
            isLastMessage={isLastMessage}
            botAvatarUrl={botAvatarUrl}
            userInitials={userInitials}
            userAvatarUrl={userAvatarUrl}
            previewData={previewData}
            previewLoading={previewLoading}
            previewError={previewError}
            onFetchPreview={onFetchPreview}
            onApprove={onApprove}
            onDeny={onDeny}
            isBusy={isBusy}
            apiBaseUrl={apiBaseUrl}
          />
        );
      }}
      increaseViewportBy={{ top: 100, bottom: 100 }}
      // Stick to the bottom on new output ONLY when the user is already at the
      // bottom. If they've scrolled up to read history during streaming, return
      // `false` so we don't yank them back down.
      followOutput={(isAtBottom: boolean) => (isAtBottom ? 'smooth' : false)}
    />
  );
}

export const ChatMessageList = memo(ChatMessageListComponent);
